import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './SignIn.css';
import useApi from '../../../shared/hooks/useApi';
import { checkForEmailParameter, handleAutoLogin } from '../../utils/autoLogin';
import { useMediaQuery, useTheme } from '@mui/material';

const SignInLeadgen = ({ setLoading }) => {
  const navigate = useNavigate();
  const { apiGet, apiPost } = useApi(); // Destructure apiGet from useApi

  // Dashboard info boxes data
  const dashboardInfoBoxes = [
    {
      title: "📊 Day-Ahead Price Analysis",
      description: "Real-time electricity and gas price monitoring with optimization insights",
      details: [
        "Live price charts for electricity and gas markets",
        "Cost comparison between different energy sources",
        "Optimization recommendations based on current prices",
        "Historical price trends and patterns"
      ],
      icon: "📈",
      color: "#1976d2"
    },
    {
      title: "⚡ Current Hour Recommendation",
      description: "Smart system that tells you which boiler to use right now",
      details: [
        "Real-time analysis of current energy prices",
        "Automatic switching recommendations between E-Boiler and Gas Boiler",
        "Cost savings calculations for each decision",
        "Live updates every hour"
      ],
      icon: "🎯",
      color: "#2e7d32"
    },
    {
      title: "⏰ Boiler Dispatch Timeline",
      description: "Visual timeline showing when each boiler was active",
      details: [
        "24-hour history of boiler usage decisions",
        "Color-coded timeline (Blue = E-Boiler, Red = Gas Boiler)",
        "Statistics on usage patterns and efficiency",
        "Current hour indicator with 'NOW' marker"
      ],
      icon: "🕐",
      color: "#ed6c02"
    },
    {
      title: "🔧 API Configuration",
      description: "Integration tools for external systems and applications",
      details: [
        "REST API endpoint for data access",
        "Secure API key authentication",
        "Real-time data streaming capabilities",
        "JSON payload templates for easy integration"
      ],
      icon: "🔌",
      color: "#7c4dff"
    },
    {
      title: "⚙️ Settings & Configuration",
      description: "Customizable parameters for your energy optimization",
      details: [
        "Country-specific energy pricing",
        "Boiler efficiency settings",
        "Grid cost configurations",
        "CO2 pricing options (live or fixed)"
      ],
      icon: "🎛️",
      color: "#e53935"
    }
  ];

  useEffect(() => {
    const checkAndHandleAutoLogin = async () => {
      const { email, job_title, company, phone, name } = checkForEmailParameter();

      if (email) {
        setLoading(true);
        const result = await handleAutoLogin(email, job_title, company, phone, name);
        if (result.success) {
          // Store the email in localStorage for future reference
          localStorage.setItem('useremail', 'leadgen@entras.be');
          localStorage.setItem('selectedProject', 'esid-25-013');
          localStorage.setItem('allowedProjects', JSON.stringify(['esid-25-013']));
          localStorage.setItem('all_roles', JSON.stringify(['operator']));
          localStorage.setItem('allowedPages', JSON.stringify(['/esid-25-013/dashboard']));
          localStorage.setItem('userrealemail', email);
          try {
            const response2 = await apiPost("UX_HTTP_API", "/esid-25-013/add-lead", {
              email: email, job_title: job_title, company: company, phone: phone, name: name
            });
            if (response2?.results) {
              localStorage.setItem('api_key', response2?.results?.api_key);
            }
            navigate('/esid-25-013/dashboard');
          } catch (apiError) {
            console.error('Add lead API call failed:', apiError);
            // Still navigate to dashboard even if API call fails
            navigate('/esid-25-013/dashboard');
          }
        } else {
          console.error('Automatic login failed from SignIn:', result.message);
        }
        setLoading(false);
      }
    };

    checkAndHandleAutoLogin();
  }, [navigate, setLoading]);


  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <div className="signin-wrapper">
      <div style={{
        width: isSmall ? '100%' : '80%',
        maxWidth: 1200,
        height: '100%',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: isSmall ? '16px' : '24px',
        padding: isSmall ? '20px' : '40px',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          pointerEvents: 'none'
        }} />

        <div className="signin-header" style={{
          textAlign: 'center',
          marginBottom: '40px',
          color: 'white',
          position: 'relative',
          zIndex: 2
        }}>
          <h2 style={{
            color: 'white',
            marginBottom: '15px',
            fontSize: isSmall ? '22px' : '28px',
            fontWeight: '700',
            textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
            letterSpacing: '-0.5px'
          }}>Welcome Back</h2>
          <p style={{
            color: 'rgba(255, 255, 255, 0.95)',
            fontSize: isSmall ? '14px' : '16px',
            fontWeight: '400',
            textShadow: '0 1px 5px rgba(0, 0, 0, 0.2)'
          }}>Your dashboard is loading... Here's what you'll see:</p>
        </div>

        <div className="dashboard-info-container" style={{
          margin: '30px 0',
          background: 'rgba(255, 255, 255, 0.4)',
          borderRadius: isSmall ? '16px' : '24px',
          padding: isSmall ? '20px' : '40px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1), 0 8px 16px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
          border: '1px solid rgba(255, 255, 255, 0.4)',
          backdropFilter: 'blur(20px)',
          width: '100%',
          position: 'relative',
          zIndex: 2
        }}>
          {/* Show all 5 info boxes at once */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '24px',
            marginBottom: '32px'
          }}>
            {dashboardInfoBoxes.map((infoBox, index) => (
              <div key={index} style={{
                background: 'linear-gradient(145deg, #ffffff, #f8f9fa)',
                borderRadius: '20px',
                padding: isSmall ? '20px' : '28px',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.9)',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                width: '100%',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                minHeight: isSmall ? '100px' : '120px'
              }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.12), 0 8px 20px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.9)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.9)';
                }}>
                <h3 style={{
                  margin: 0,
                  color: '#2c3e50',
                  fontSize: isSmall ? '16px' : '20px',
                  fontWeight: '800',
                  lineHeight: '1.3',
                  background: 'linear-gradient(135deg, #2c3e50, #34495e)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  textAlign: 'center',
                  maxWidth: '100%',
                  wordWrap: 'break-word',
                  hyphens: 'auto',
                  letterSpacing: '-0.2px'
                }}>{infoBox.title}</h3>
              </div>
            ))}
          </div>

          {/* Loading Message */}

        </div>
      </div>
      {/* */}
    </div>
  );
};

export default SignInLeadgen;